import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Copy, RadioTower, Search, ShieldCheck } from 'lucide-react';
import { Button, cn, StatusBadge } from '@mpange/ui';

export function Eyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cn('text-xs font-semibold uppercase tracking-[0.18em] text-brand', className)}>
      {children}
    </p>
  );
}

export function SectionIntro({
  eyebrow,
  title,
  description,
  align = 'left',
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: 'left' | 'center';
}) {
  return (
    <div className={cn('max-w-3xl', align === 'center' && 'mx-auto text-center')}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="mt-3 text-3xl font-semibold leading-tight tracking-tight text-text md:text-4xl">
        {title}
      </h2>
      {description && <p className="mt-4 text-base leading-7 text-text-muted md:text-lg">{description}</p>}
    </div>
  );
}

export function PremiumPanel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-lg-token border border-border bg-surface shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function MetricTile({
  label,
  value,
  detail,
  status,
}: {
  label: string;
  value: string;
  detail?: string;
  status?: string;
}) {
  return (
    <div className="border-b border-border px-4 py-4 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</p>
        {status && <StatusBadge status={status} />}
      </div>
      <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-text">{value}</p>
      {detail && <p className="mt-1 text-xs text-text-muted">{detail}</p>}
    </div>
  );
}

export function InfrastructureMap() {
  const nodes = [
    { x: 58, y: 92, label: 'SME', meta: 'retail campaigns' },
    { x: 168, y: 58, label: 'Fintech', meta: 'OTP + alerts' },
    { x: 175, y: 150, label: 'School', meta: 'parent updates' },
    { x: 330, y: 104, label: 'MPANGE', meta: 'routing + wallet', primary: true },
    { x: 498, y: 52, label: 'SMS', meta: 'MNO routes' },
    { x: 565, y: 130, label: 'USSD', meta: 'menus' },
    { x: 465, y: 178, label: 'Voice', meta: 'IVR' },
    { x: 650, y: 92, label: 'Mobile money', meta: 'TZ rails' },
  ];
  const lines = [
    [80, 96, 300, 108],
    [195, 68, 305, 105],
    [205, 150, 305, 114],
    [366, 98, 490, 58],
    [370, 110, 552, 130],
    [365, 120, 462, 173],
    [580, 130, 638, 98],
  ];

  return (
    <div className="relative overflow-hidden rounded-lg-token border border-border bg-[#0A0D14] p-3 text-white">
      <svg viewBox="0 0 720 240" className="h-[300px] w-full md:h-[360px]" role="img" aria-label="MPANGE communication infrastructure map">
        <defs>
          <linearGradient id="mpangeLine" x1="0" x2="1">
            <stop stopColor="#6E86FF" stopOpacity="0.2" />
            <stop offset="0.5" stopColor="#6E86FF" stopOpacity="0.9" />
            <stop offset="1" stopColor="#14804A" stopOpacity="0.35" />
          </linearGradient>
          <filter id="softGlow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <rect width="720" height="240" fill="#0A0D14" />
        <path d="M0 210 C120 150 200 230 330 172 C470 110 540 200 720 118" fill="none" stroke="#1d2433" strokeWidth="1" />
        {lines.map(([x1, y1, x2, y2], index) => (
          <line key={index} x1={x1} y1={y1} x2={x2} y2={y2} stroke="url(#mpangeLine)" strokeWidth="1.5" strokeDasharray="5 8" className="network-flow" style={{ animationDelay: `${index * 120}ms` }} />
        ))}
        {nodes.map((node) => (
          <g key={node.label} transform={`translate(${node.x} ${node.y})`}>
            <circle r={node.primary ? 34 : 24} fill={node.primary ? '#111A3D' : '#111318'} stroke={node.primary ? '#6E86FF' : '#3B4252'} strokeWidth="1.2" filter={node.primary ? 'url(#softGlow)' : undefined} />
            <circle r={node.primary ? 4 : 3} fill={node.primary ? '#6E86FF' : '#98A2B3'} />
            <text y={node.primary ? 54 : 42} textAnchor="middle" fill="#F5F7FA" fontSize={node.primary ? 14 : 12} fontWeight="600">{node.label}</text>
            <text y={node.primary ? 72 : 58} textAnchor="middle" fill="#98A2B3" fontSize="10">{node.meta}</text>
          </g>
        ))}
      </svg>
      <div className="grid border-t border-white/10 md:grid-cols-4">
        {['Idempotent APIs', 'Provider failover', 'Signed webhooks', 'Immutable ledger'].map((item) => (
          <div key={item} className="flex items-center gap-2 border-white/10 px-4 py-3 text-xs text-slate-300 md:border-r md:last:border-r-0">
            <CheckCircle2 className="h-3.5 w-3.5 text-[#6E86FF]" />
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProductConsoleMock() {
  return (
    <PremiumPanel className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <p className="text-xs font-medium text-text-muted">Campaign workspace</p>
          <p className="text-sm font-semibold text-text">September school fee reminders</p>
        </div>
        <StatusBadge status="processing" />
      </div>
      <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
        <div className="space-y-4 border-b border-border p-4 lg:border-b-0 lg:border-r">
          <div className="grid grid-cols-3 rounded-md-token border border-border">
            <MetricTile label="Recipients" value="18,240" detail="3 groups selected" />
            <MetricTile label="Segments" value="1.3" detail="avg per message" />
            <MetricTile label="Cost" value="TZS 547k" detail="reserved" />
          </div>
          <div className="rounded-md-token border border-border bg-surface-muted/50 p-3">
            <div className="mb-2 flex items-center justify-between text-xs text-text-muted">
              <span>Delivery trend</span>
              <span>last 6 hours</span>
            </div>
            <div className="flex h-28 items-end gap-1.5" aria-hidden>
              {[36, 48, 42, 72, 62, 88, 78, 95, 82, 101, 94, 112].map((h, i) => (
                <div key={i} className="flex-1 rounded-sm-token bg-brand-soft" style={{ height: `${h}px` }}>
                  <div className="h-1/2 rounded-sm-token bg-brand/80" />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-surface-muted/40 p-4">
          <div className="mx-auto max-w-[260px] rounded-[28px] border border-border bg-surface p-3 shadow-dropdown">
            <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-border" />
            <div className="rounded-lg-token bg-surface-muted p-3">
              <p className="text-xs font-medium text-text">MPANGE</p>
              <p className="mt-2 text-sm text-text-muted">
                Habari {`{{first_name}}`}, ada ya muhula inatakiwa kulipwa kabla ya Ijumaa. Ref: {`{{student_id}}`}
              </p>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-text-muted">
              <span className="rounded-sm-token border border-border px-2 py-1">GSM-7</span>
              <span className="rounded-sm-token border border-border px-2 py-1">2 vars</span>
            </div>
          </div>
        </div>
      </div>
    </PremiumPanel>
  );
}

export function BuilderCanvasMock({ mode = 'ussd' }: { mode?: 'ussd' | 'voice' }) {
  const title = mode === 'ussd' ? 'USSD Loan Eligibility Flow' : 'IVR Payment Reminder Flow';
  return (
    <PremiumPanel className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <p className="text-xs text-text-muted">{mode === 'ussd' ? '*150*88# service' : '+255 22 400 1180'}</p>
          <p className="text-sm font-semibold text-text">{title}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status="draft" />
          <Button size="sm" variant="secondary">Simulate</Button>
          <Button size="sm">Publish</Button>
        </div>
      </div>
      <div className="grid min-h-[460px] lg:grid-cols-[1fr_300px]">
        <div className="relative overflow-hidden bg-[linear-gradient(var(--color-border)_1px,transparent_1px),linear-gradient(90deg,var(--color-border)_1px,transparent_1px)] bg-[length:28px_28px] p-5">
          <div className="absolute left-4 top-4 flex gap-1 rounded-md-token border border-border bg-surface p-1">
            {['75%', 'Fit', 'Snap'].map((item) => <span key={item} className="px-2 py-1 text-xs text-text-muted">{item}</span>)}
          </div>
          <svg className="absolute inset-0 h-full w-full" aria-hidden>
            <path d="M180 145 C260 145 250 230 330 230" fill="none" stroke="var(--color-brand)" strokeWidth="1.5" />
            <path d="M430 230 C510 230 500 145 580 145" fill="none" stroke="var(--color-brand)" strokeWidth="1.5" />
            <path d="M430 230 C510 230 500 320 580 320" fill="none" stroke="var(--color-warning)" strokeWidth="1.5" />
          </svg>
          <CanvasNode x="70px" y="106px" title="Welcome" meta="CON text" lines={['1. Apply loan', '2. Check balance']} />
          <CanvasNode x="315px" y="191px" title="Collect NIDA" meta="input validation" lines={['10 digits', 'retry x2']} />
          <CanvasNode x="560px" y="106px" title="Eligible" meta="API lookup" lines={['offer amount', 'accept terms']} />
          <CanvasNode x="560px" y="282px" title="Not eligible" meta="terminal" lines={['reason message', 'support contact']} />
        </div>
        <aside className="border-t border-border bg-surface p-4 lg:border-l lg:border-t-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Inspector</p>
          <div className="mt-4 space-y-4">
            <label className="block text-xs font-medium text-text">Node label
              <input className="mt-1 h-10 w-full rounded-md-token border border-border bg-surface px-3 text-sm" defaultValue={mode === 'ussd' ? 'Collect NIDA' : 'Collect digit'} />
            </label>
            <label className="block text-xs font-medium text-text">Validation
              <select className="mt-1 h-10 w-full rounded-md-token border border-border bg-surface px-3 text-sm">
                <option>Required numeric input</option>
              </select>
            </label>
            <div className="rounded-md-token border border-warning/30 bg-warning-soft p-3 text-xs text-warning">
              Missing fallback route for timeout. Add a retry or terminal message before publishing.
            </div>
            <div className="rounded-md-token border border-border bg-surface-muted p-3">
              <p className="text-xs font-medium text-text">Mobile preview</p>
              <pre className="mt-2 whitespace-pre-wrap font-mono text-xs text-text-muted">CON Karibu MPANGE{'\n'}1. Apply loan{'\n'}2. Check balance</pre>
            </div>
          </div>
        </aside>
      </div>
    </PremiumPanel>
  );
}

function CanvasNode({ x, y, title, meta, lines }: { x: string; y: string; title: string; meta: string; lines: string[] }) {
  return (
    <div className="absolute w-[190px] rounded-md-token border border-border bg-surface shadow-dropdown" style={{ left: x, top: y }}>
      <div className="border-b border-border px-3 py-2">
        <p className="text-sm font-semibold text-text">{title}</p>
        <p className="text-[11px] uppercase tracking-wide text-text-muted">{meta}</p>
      </div>
      <div className="space-y-1 px-3 py-2">
        {lines.map((line) => <p key={line} className="text-xs text-text-muted">{line}</p>)}
      </div>
    </div>
  );
}

export function DeveloperConsoleMock() {
  const code = `curl https://api.mpange.co.tz/v1/sms/messages \\
  -H "Authorization: Bearer mp_live_..." \\
  -H "Idempotency-Key: msg_2026_09_04_001" \\
  -d '{
    "to": "+255683859574",
    "from": "MPANGE",
    "message": "Your payment of TZS 42,000 was received."
  }'`;
  return (
    <PremiumPanel className="overflow-hidden bg-code text-code">
      <div className="flex items-center justify-between border-b border-code px-4 py-3">
        <div className="flex gap-1.5" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full bg-danger" />
          <span className="h-2.5 w-2.5 rounded-full bg-warning" />
          <span className="h-2.5 w-2.5 rounded-full bg-success" />
        </div>
        <button className="inline-flex items-center gap-1.5 rounded-sm-token border border-code px-2 py-1 text-xs text-slate-300">
          <Copy className="h-3 w-3" /> Copy
        </button>
      </div>
      <pre className="overflow-x-auto p-5 font-mono text-xs leading-6 text-slate-200">{code}</pre>
      <div className="border-t border-code bg-black/20 p-4">
        <p className="font-mono text-xs text-success">202 queued</p>
        <p className="mt-1 font-mono text-xs text-slate-400">segments: 1 · estimated_cost: TZS 32 · webhook: signed</p>
      </div>
    </PremiumPanel>
  );
}

export function CommandBar({ placeholder = 'Search messages, campaigns, contacts...' }: { placeholder?: string }) {
  return (
    <div className="hidden h-9 min-w-[320px] items-center gap-2 rounded-md-token border border-border bg-surface-muted px-3 text-sm text-text-muted lg:flex">
      <Search className="h-4 w-4" />
      <span className="flex-1">{placeholder}</span>
      <kbd className="rounded-sm-token border border-border bg-surface px-1.5 py-0.5 font-mono text-[11px]">⌘K</kbd>
    </div>
  );
}

export function TrustBand() {
  return (
    <div className="grid border-y border-border bg-surface md:grid-cols-4">
      {[
        ['TZ-first routing', 'Built around local MNO and payment realities'],
        ['Ledger-grade billing', 'Reservations, charges, invoices and audit'],
        ['API-first controls', 'Every workflow designed for UI and API use'],
        ['Provider independent', 'Africa’s Talking today, direct routes tomorrow'],
      ].map(([title, body]) => (
        <div key={title} className="border-b border-border px-6 py-5 md:border-b-0 md:border-r md:last:border-r-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-text">
            {title === 'Provider independent' ? <RadioTower className="h-4 w-4 text-brand" /> : <ShieldCheck className="h-4 w-4 text-brand" />}
            {title}
          </div>
          <p className="mt-2 text-sm text-text-muted">{body}</p>
        </div>
      ))}
    </div>
  );
}

export function CtaBand() {
  return (
    <section className="border-t border-border bg-surface">
      <div className="mx-auto grid max-w-7xl gap-8 px-6 py-16 lg:grid-cols-[1fr_auto] lg:items-center">
        <div>
          <Eyebrow>Start building</Eyebrow>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-text">Run customer communications with infrastructure discipline.</h2>
          <p className="mt-3 max-w-2xl text-text-muted">Create an organization, fund a wallet, connect APIs, and start sending through a platform built for Tanzanian operations.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/register"><Button size="lg">Create account <ArrowRight className="h-4 w-4" /></Button></Link>
          <Link to="/contact"><Button size="lg" variant="secondary">Contact sales</Button></Link>
        </div>
      </div>
    </section>
  );
}
